import {Component, input, signal, output, OnInit, computed} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MultiSelectModule} from 'primeng/multiselect';
import {Button} from 'primeng/button';
import {Chip} from 'primeng/chip';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  IntegrationConfigOutput,
  ModuleIntegrationConfigVM,
} from './module-integration-config.types';
import {DirectusTranslatedItem, DirectusTranslation} from '@frontend/directus';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ModelFieldMultiselectComponent} from '../../../../../../../../shared/components/inputs/model-field/model-field-multiselect/model-field-multiselect.component';
import {ModelFieldSelectComponent} from '../../../../../../../../shared/components/inputs/model-field/model-field-select/model-field-select.component';

@Component({
  selector: 'app-module-integration-config',
  imports: [
    FormsModule,
    MultiSelectModule,
    Button,
    Chip,
    TranslocoPipe,
    TranslateDirectusPipe,
    ModelFieldMultiselectComponent,
    ModelFieldSelectComponent,
  ],
  templateUrl: './module-integration-config.component.html',
})
export class ModuleIntegrationConfigComponent implements OnInit {
  readonly isReadOnly = input<boolean>(false);
  readonly inputObject = input.required<ModuleIntegrationConfigVM>();
  readonly dataDomainOptions = input.required<DirectusTranslatedItem[]>();
  readonly integrationTypeOptions = input.required<DirectusTranslatedItem[]>();

  configChange = output<IntegrationConfigOutput>();

  readonly yesNoSelection = signal<boolean | undefined>(undefined);
  selectedDataDomains = signal<string[]>([]);
  selectedIntegrationType = signal<string | undefined>(undefined);

  readonly temporaryIdentifier = computed(() => this.inputObject().temporaryIdentifier);

  readonly areDataDomainsMissing = computed<boolean>(
    () => this.yesNoSelection() === true && this.selectedDataDomains().length === 0,
  );

  readonly isIntegrationTypeMissing = computed<boolean>(
    () => this.yesNoSelection() === true && !this.selectedIntegrationType(),
  );

  readonly isValid = computed<boolean>(() => {
    const yesNoSelection = this.yesNoSelection();

    if (yesNoSelection === undefined) {
      return false;
    }

    if (!yesNoSelection) {
      return true;
    }

    return this.selectedDataDomains().length > 0 && !!this.selectedIntegrationType();
  });

  ngOnInit() {
    const current = this.inputObject();

    this.yesNoSelection.set(current.integrationRequired);
    this.selectedDataDomains.set(current.dataDomainDirectusUuids);
    this.selectedIntegrationType.set(current.integrationTypeUuid);
  }

  onYesNoToggle(value: boolean): void {
    this.yesNoSelection.set(value);
    if (!value) {
      this.selectedDataDomains.set([]);
      this.selectedIntegrationType.set(undefined);
    }
    this.emitChange();
  }

  removeDataDomain(domainId: string): void {
    this.selectedDataDomains.update((prev) => prev.filter((id) => id !== domainId));
    this.emitChange();
  }

  getDataDomainLabel(domainId: string): DirectusTranslation[] {
    return this.dataDomainOptions().find((opt) => opt.id === domainId)?.translations ?? [];
  }

  emitChange(): void {
    const current = this.inputObject();
    const output: IntegrationConfigOutput = {
      temporaryIdentifier: current.temporaryIdentifier,
      flowDirection: current.flowDirection,
      systemRecordingPks: current.systemRecordingPks,
      data: {
        yesNoSelection: this.yesNoSelection()!,
        dataDomainDirectusUuids: this.selectedDataDomains(),
        integrationTypeId: this.selectedIntegrationType(),
      },
      isValid: this.isValid(),
    };
    this.configChange.emit(output);
  }
}
